import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { format, parseISO, subDays } from "date-fns";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  BarChart, Bar, ReferenceLine,
} from "recharts";
import { api } from "@/lib/api";
import type { DailyLog, WeeklyLog, Lab } from "@/lib/types";

export function Dashboard() {
  const [daily, setDaily] = useState<DailyLog[]>([]);
  const [weekly, setWeekly] = useState<WeeklyLog[]>([]);
  const [labs, setLabs] = useState<Lab[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.dailyList(90), api.weeklyList(), api.labsList()])
      .then(([d, w, l]) => { setDaily(d); setWeekly(w); setLabs(l); })
      .finally(() => setLoading(false));
  }, []);

  const today = format(new Date(), "yyyy-MM-dd");
  const todayLog = daily.find((d) => d.date === today);
  const last7 = daily.filter((d) => d.date >= format(subDays(new Date(), 6), "yyyy-MM-dd"));

  const streak = useMemo(() => {
    let s = 0;
    for (let i = 0; i < 60; i++) {
      const d = format(subDays(new Date(), i), "yyyy-MM-dd");
      if (daily.some((x) => x.date === d)) s++; else break;
    }
    return s;
  }, [daily]);

  const avg = (key: keyof DailyLog) => {
    const vals = last7.map((d) => d[key]).filter((v): v is number => typeof v === "number");
    if (!vals.length) return null;
    return vals.reduce((a, b) => a + b, 0) / vals.length;
  };

  const runTotal7 = last7.reduce((acc, d) => acc + (d.run_km ?? 0), 0);
  const liftDays7 = last7.filter((d) => d.lift_session && !["rest", "cardio_only"].includes(d.lift_session)).length;
  const learningTotal7 = last7.reduce((acc, d) => acc + (d.learning_min ?? 0), 0);
  const tshLabs = labs.filter((l) => l.test_name.toLowerCase() === "tsh");

  if (loading) return <p className="text-muted">Loading…</p>;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Today</h1>
          <p className="text-sm text-muted">{format(new Date(), "EEEE, d MMM yyyy")}</p>
        </div>
        <Link to="/log" className={todayLog ? "btn-ghost" : "btn-primary"}>
          {todayLog ? "Edit today's log" : "Log today →"}
        </Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Streak" value={`${streak}d`} sub="consecutive logs" />
        <Kpi label="Sleep avg 7d" value={fmt(avg("sleep_hours"), "h", 1)} sub="target 7.5" flag={warn(avg("sleep_hours"), 7, 7.5, true)} />
        <Kpi label="Thyronorm gap 7d" value={fmt(avg("thyronorm_gap_min"), "m")} sub="target ≥75" flag={warn(avg("thyronorm_gap_min"), 60, 75, true)} />
        <Kpi label="Protein avg 7d" value={fmt(avg("protein_g"), "g")} sub="target 130" flag={warn(avg("protein_g"), 100, 130, true)} />
        <Kpi label="Run last 7d" value={`${runTotal7.toFixed(1)} km`} sub="building to 42" />
        <Kpi label="Lift days 7d" value={`${liftDays7}/3`} sub="Mon/Wed/Fri" flag={liftDays7 < 2 ? "warn" : "ok"} />
        <Kpi label="Screen avg 7d" value={fmt(avg("screen_time_min"), "m")} sub="target <240" flag={warn(avg("screen_time_min"), 240, 180)} />
        <Kpi label="Learning 7d" value={`${learningTotal7}m`} sub="target 420/wk" flag={learningTotal7 < 300 ? "warn" : "ok"} />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <ChartCard title="Thyronorm → food gap (min)" desc="Must be ≥75. This one number drives your energy.">
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={daily}>
              <CartesianGrid stroke="#1f1f1f" />
              <XAxis dataKey="date" hide />
              <YAxis stroke="#737373" />
              <Tooltip contentStyle={chartTooltip} />
              <ReferenceLine y={75} stroke="#10b981" strokeDasharray="4 4" />
              <Line type="monotone" dataKey="thyronorm_gap_min" stroke="#10b981" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Sleep hours" desc="Target 7.5h. Phone out of bedroom by 11:30.">
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={daily}>
              <CartesianGrid stroke="#1f1f1f" />
              <XAxis dataKey="date" hide />
              <YAxis stroke="#737373" domain={[0, 10]} />
              <Tooltip contentStyle={chartTooltip} />
              <ReferenceLine y={7.5} stroke="#10b981" strokeDasharray="4 4" />
              <Line type="monotone" dataKey="sleep_hours" stroke="#10b981" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Run distance (km/day)" desc="80% Zone 2, long run weekends.">
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={daily}>
              <CartesianGrid stroke="#1f1f1f" />
              <XAxis dataKey="date" hide />
              <YAxis stroke="#737373" />
              <Tooltip contentStyle={chartTooltip} />
              <Bar dataKey="run_km" fill="#10b981" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Weight & waist (weekly)" desc="Target Dec: 60 kg at ~12% BF.">
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={weekly}>
              <CartesianGrid stroke="#1f1f1f" />
              <XAxis dataKey="week_ending" hide />
              <YAxis yAxisId="l" stroke="#10b981" />
              <YAxis yAxisId="r" orientation="right" stroke="#f59e0b" />
              <Tooltip contentStyle={chartTooltip} />
              <Line yAxisId="l" type="monotone" dataKey="weight_kg" stroke="#10b981" strokeWidth={2} />
              <Line yAxisId="r" type="monotone" dataKey="waist_cm" stroke="#f59e0b" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Screen time (min)" desc="Lower is better. Reels blocked.">
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={daily}>
              <CartesianGrid stroke="#1f1f1f" />
              <XAxis dataKey="date" hide />
              <YAxis stroke="#737373" />
              <Tooltip contentStyle={chartTooltip} />
              <ReferenceLine y={240} stroke="#f59e0b" strokeDasharray="4 4" />
              <Bar dataKey="screen_time_min" fill="#ef4444" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Mood (1–10)" desc="Trailing trend, not a single day.">
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={daily}>
              <CartesianGrid stroke="#1f1f1f" />
              <XAxis dataKey="date" hide />
              <YAxis domain={[1, 10]} stroke="#737373" />
              <Tooltip contentStyle={chartTooltip} />
              <Line type="monotone" dataKey="mood" stroke="#10b981" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {tshLabs.length > 0 && (
        <ChartCard title="TSH trend" desc="Target 1.0–2.5 for full symptom relief.">
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={tshLabs.map((l) => ({ date: l.test_date, tsh: l.value }))}>
              <CartesianGrid stroke="#1f1f1f" />
              <XAxis dataKey="date" stroke="#737373" />
              <YAxis stroke="#737373" />
              <Tooltip contentStyle={chartTooltip} />
              <ReferenceLine y={2.5} stroke="#10b981" strokeDasharray="4 4" label={{ value: "target", fill: "#10b981", fontSize: 10 }} />
              <ReferenceLine y={4.2} stroke="#f59e0b" strokeDasharray="4 4" label={{ value: "upper", fill: "#f59e0b", fontSize: 10 }} />
              <Line type="monotone" dataKey="tsh" stroke="#10b981" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      )}

      <div className="card">
        <h3 className="font-semibold mb-3">Recent notes</h3>
        <div className="space-y-2">
          {daily.slice(-10).reverse().filter((d) => d.note).map((d) => (
            <div key={d.date} className="text-sm flex gap-3 border-b border-border pb-2">
              <span className="text-muted font-mono w-24">{format(parseISO(d.date), "d MMM")}</span>
              <span>{d.note}</span>
            </div>
          ))}
          {daily.filter((d) => d.note).length === 0 && (
            <p className="text-sm text-muted">Notes will appear here once you log.</p>
          )}
        </div>
      </div>
    </div>
  );
}

const chartTooltip = { background: "#111", border: "1px solid #1f1f1f", fontSize: "12px" };

function Kpi({
  label, value, sub, flag,
}: { label: string; value: string; sub: string; flag?: "ok" | "warn" | "bad" }) {
  const color = flag === "bad" ? "text-danger" : flag === "warn" ? "text-warn" : "text-accent";
  return (
    <div className="card">
      <div className="text-xs text-muted uppercase tracking-wider">{label}</div>
      <div className={`text-2xl font-mono font-semibold ${color}`}>{value}</div>
      <div className="text-xs text-muted">{sub}</div>
    </div>
  );
}

function ChartCard({ title, desc, children }: { title: string; desc: string; children: React.ReactNode }) {
  return (
    <div className="card">
      <h3 className="font-semibold">{title}</h3>
      <p className="text-xs text-muted mb-3">{desc}</p>
      {children}
    </div>
  );
}

function fmt(v: number | null, suffix = "", dp = 0) {
  if (v === null || isNaN(v)) return "—";
  return `${v.toFixed(dp)}${suffix}`;
}

function warn(v: number | null, warnAt: number, targetAt: number, higherBetter = false): "ok" | "warn" | "bad" | undefined {
  if (v === null) return undefined;
  if (higherBetter) {
    if (v >= targetAt) return "ok";
    if (v >= warnAt) return "warn";
    return "bad";
  }
  if (v <= targetAt) return "ok";
  if (v <= warnAt) return "warn";
  return "bad";
}
